/**
 * Tableau de bord commercial — CA « proxy » = somme des budgets projets signés (CAF).
 * Objectifs mensuels lus depuis la base (voir {@link hydraCaObjectifsToByUserId}).
 */

/** @param {unknown} ref */
export function extractIdRef(ref) {
    if (ref == null) return null;
    if (typeof ref === 'number') return Number.isFinite(ref) ? ref : null;
    if (typeof ref === 'object') {
        if (ref.id != null) return Number(ref.id);
        if (typeof ref['@id'] === 'string') {
            const m = ref['@id'].match(/\/(\d+)$/);
            return m ? parseInt(m[1], 10) : null;
        }
    }
    if (typeof ref === 'string') {
        const m = ref.match(/\/(\d+)$/);
        return m ? parseInt(m[1], 10) : null;
    }
    return null;
}

export function ymKey(year, monthIndex) {
    return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
}

/**
 * @param {Array<{commercial?:unknown,moisRef?:string,montantObjectif?:unknown}>} hydraMember
 * @returns {Record<number, Record<string, number>>} userId → { « YYYY-MM » → euros }
 */
export function hydraCaObjectifsToByUserId(hydraMember) {
    /** @type {Record<number, Record<string, number>>} */
    const out = {};
    if (!hydraMember || !Array.isArray(hydraMember)) return out;
    hydraMember.forEach((row) => {
        const uid = extractIdRef(row.commercial);
        if (uid == null || !Number.isFinite(uid)) return;
        const ym = row.moisRef;
        if (typeof ym !== 'string' || ym.length !== 7) return;
        const amt = typeof row.montantObjectif === 'string'
            ? parseFloat(row.montantObjectif)
            : Number(row.montantObjectif);
        if (!Number.isFinite(amt) || amt < 0) return;
        if (!out[uid]) out[uid] = {};
        out[uid][ym] = amt;
    });
    return out;
}

/**
 * Somme des objectifs mensuels effectivement renseignés pour une année (janv.→déc.).
 * @returns {{ sum: number, count: number }}
 */
export function summarizeAnnualObjectifsFromMap(monthMap, year) {
    let sum = 0;
    let count = 0;
    if (!monthMap || typeof monthMap !== 'object') return { sum, count };
    for (let m = 0; m < 12; m += 1) {
        const k = ymKey(year, m);
        const v = monthMap[k];
        if (v != null && Number.isFinite(Number(v))) {
            sum += Number(v);
            count += 1;
        }
    }
    return { sum, count };
}

export function projetCaBudgetEuros(p) {
    const b = p.budget;
    if (b == null || b === '') return 0;
    const n = parseFloat(String(b).replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
}

const isoSigned = (p) => p.statut === 'signe' || p.statut === 'valide';

/** Date de rattachement CA projet (salon / remise / création). */
export function projetAttributionDate(p) {
    const ref = p.montageDebut || p.remiseClient || p.createdAt || p.updatedAt;
    return ref ? new Date(ref) : null;
}

/**
 * Réalisé CA du mois calendaire (tous les projets signés dont la date de rattachement
 * tombe dans ce mois), indépendamment de la date de saisie de l’objectif — comportement « rétroactif » côté suivi.
 * @param {object[]} projetPole déjà filtrés (ex. CAF = commercial)
 */
export function sumSignedCaForCommercialMonth(projetPole, year, monthIndex) {
    let sum = 0;
    projetPole.forEach((p) => {
        if (!isoSigned(p)) return;
        const d = projetAttributionDate(p);
        if (!d || Number.isNaN(d.getTime())) return;
        if (d.getFullYear() === year && d.getMonth() === monthIndex) {
            sum += projetCaBudgetEuros(p);
        }
    });
    return sum;
}

export function sumSignedCaForCommercialYear(projetPole, year) {
    let sum = 0;
    projetPole.forEach((p) => {
        if (!isoSigned(p)) return;
        const d = projetAttributionDate(p);
        if (!d || Number.isNaN(d.getTime())) return;
        if (d.getFullYear() === year) sum += projetCaBudgetEuros(p);
    });
    return sum;
}

export function pctVersObjectif(realise, objectif) {
    if (objectif == null || !(objectif > 0)) return null;
    return Math.round((realise / objectif) * 1000) / 10;
}

/** Statuts considérés comme « pipe » (dossiers en cours, ni signés ni perdus). */
const isPipe = (p) => p.statut === 'en_cours';

/**
 * « Pipe » chiffré = somme brute des budgets des projets en cours (non signés, non perdus).
 * @param {object[]} projetPole déjà filtrés (ex. CAF = commercial)
 */
export function pipeBudgetForCommercial(projetPole) {
    let sum = 0;
    if (!Array.isArray(projetPole)) return sum;
    projetPole.forEach((p) => {
        if (isPipe(p)) sum += projetCaBudgetEuros(p);
    });
    return sum;
}

/** Nombre de dossiers comptant dans le pipe. */
export function pipeCountForCommercial(projetPole) {
    if (!Array.isArray(projetPole)) return 0;
    return projetPole.reduce((n, p) => (isPipe(p) ? n + 1 : n), 0);
}

/**
 * Objectifs ANNUELS lus depuis /api/ca_objectif_annuels.
 * @param {Array<{commercial?:unknown,annee?:number,montantObjectif?:unknown}>} hydraMember
 * @returns {Record<number, Record<number, number>>} userId → { année → euros }
 */
export function hydraCaObjectifsAnnuelsToByUserId(hydraMember) {
    /** @type {Record<number, Record<number, number>>} */
    const out = {};
    if (!hydraMember || !Array.isArray(hydraMember)) return out;
    hydraMember.forEach((row) => {
        const uid = extractIdRef(row.commercial);
        if (uid == null || !Number.isFinite(uid)) return;
        const an = Number(row.annee);
        if (!Number.isFinite(an)) return;
        const amt = typeof row.montantObjectif === 'string'
            ? parseFloat(row.montantObjectif)
            : Number(row.montantObjectif);
        if (!Number.isFinite(amt) || amt < 0) return;
        if (!out[uid]) out[uid] = {};
        out[uid][an] = amt;
    });
    return out;
}

export const fmtEuroCommercialDash = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(n) || 0);

/**
 * Construit un bloc « CA vs objectifs » complet pour un commercial (employé ou manager pour lui-même).
 * Inclut : mois en cours, année, objectif annuel saisi, pipe chiffré, détail mois par mois.
 * @param {{ projetsCaf: object[], monthMap?: Record<string,number>, objectifAnnuelSaisi?: number|null, year: number }} args
 */
export function buildCommercialCaBloc({ projetsCaf, monthMap = {}, objectifAnnuelSaisi = null, year }) {
    const now = new Date();
    const mIdx = now.getMonth();
    const mk = ymKey(year, mIdx);
    const objectifMois = monthMap[mk] != null && Number.isFinite(Number(monthMap[mk])) ? Number(monthMap[mk]) : null;
    const realiseMois = sumSignedCaForCommercialMonth(projetsCaf, year, mIdx);
    const realiseAnnee = sumSignedCaForCommercialYear(projetsCaf, year);
    const { sum: objectifAnneeSum, count: objectifAnneeMonths } = summarizeAnnualObjectifsFromMap(monthMap, year);
    const objAnnuel = (objectifAnnuelSaisi != null && Number.isFinite(Number(objectifAnnuelSaisi)) && Number(objectifAnnuelSaisi) > 0)
        ? Number(objectifAnnuelSaisi) : null;
    const objectifAnneeRef = objAnnuel != null ? objAnnuel : (objectifAnneeSum > 0 ? objectifAnneeSum : null);
    const pipeEuros = pipeBudgetForCommercial(projetsCaf);
    const pipeCount = pipeCountForCommercial(projetsCaf);
    const monthly = [];
    for (let m = 0; m < 12; m += 1) {
        const k = ymKey(year, m);
        const obj = monthMap[k] != null && Number.isFinite(Number(monthMap[k])) ? Number(monthMap[k]) : null;
        const real = sumSignedCaForCommercialMonth(projetsCaf, year, m);
        monthly.push({ monthIndex: m, objectif: obj, realise: real });
    }
    return {
        year,
        objectifMois,
        realiseMois,
        objectifAnneeSum,
        objectifAnneeMonths,
        objAnnuel,
        objectifAnneeRef,
        realiseAnnee,
        pipeEuros,
        pipeCount,
        monthly,
        pctMois: pctVersObjectif(realiseMois, objectifMois),
        pctAnnee: objectifAnneeRef != null ? pctVersObjectif(realiseAnnee, objectifAnneeRef) : null,
        monthLabel: now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
    };
}
